import { useEffect } from 'react';

/**
 * Lightweight per-route SEO helper.
 * Sets <title>, meta description, canonical, OG/Twitter tags without an extra dependency.
 */
export default function useSEO({ title, description, path, image, noindex = false }) {
    useEffect(() => {
        const base = 'Leiha Martin Art';
        const fullTitle = title ? `${title} · ${base}` : `${base} — Bespoke animal portraits`;
        document.title = fullTitle;

        const setMeta = (selector, attrs) => {
            let el = document.head.querySelector(selector);
            if (!el) {
                el = document.createElement('meta');
                Object.entries(attrs).forEach(([k, v]) => v && el.setAttribute(k, v));
                document.head.appendChild(el);
            }
            return el;
        };
        const upsertMetaByName = (name, content) => {
            if (!content) return;
            let el = document.head.querySelector(`meta[name="${name}"]`);
            if (!el) {
                el = document.createElement('meta');
                el.setAttribute('name', name);
                document.head.appendChild(el);
            }
            el.setAttribute('content', content);
        };
        const upsertMetaByProp = (prop, content) => {
            if (!content) return;
            let el = document.head.querySelector(`meta[property="${prop}"]`);
            if (!el) {
                el = document.createElement('meta');
                el.setAttribute('property', prop);
                document.head.appendChild(el);
            }
            el.setAttribute('content', content);
        };

        if (description) {
            upsertMetaByName('description', description);
            upsertMetaByProp('og:description', description);
            upsertMetaByName('twitter:description', description);
        }
        upsertMetaByProp('og:title', fullTitle);
        upsertMetaByName('twitter:title', fullTitle);

        if (image) {
            upsertMetaByProp('og:image', image);
            upsertMetaByName('twitter:image', image);
        }

        upsertMetaByName('robots', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large');

        // Canonical
        const origin = window.location.origin;
        const url = `${origin}${path || window.location.pathname}`;
        let canonical = document.head.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement('link');
            canonical.setAttribute('rel', 'canonical');
            document.head.appendChild(canonical);
        }
        canonical.setAttribute('href', url);
        upsertMetaByProp('og:url', url);
    }, [title, description, path, image, noindex]);
}
